import React, { useState, useEffect, useMemo } from "react";
import { useGetApiQuery } from "../../redux/services/apiHome";
import Loader from "../../components/Loader";
import InnerBannerBreadcrumb from "../../components/shared/breadcrumb/InnerBannerBreadcrumb";
import usePagination from "../../hooks/usePagination";
import Pagination from "../../components/Pagination/Pagination";
import { useLanguageTranslation } from "../../hooks/useLanguageTranslation";
import { useSelector } from "react-redux";
import {
  extractDate,
  extractDateFromStr,
  getLatestUpdatedObject,
} from "../../Utils/utils";
import PageLastUpdated from "../../components/shared/PageLastUpdated";
import TenderSearch from "../../components/TenderSearch";
import { useTableResponsive } from "../../hooks/useTableResponsive";
import useSeo from "../../hooks/useSeo";
import { useLocation } from "react-router-dom";
import PdfZipItem from "../../components/PdfZipItem";

const AwardedTenders = () => {
  const { languageType } = useSelector((state) => state.homeSlice);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredTenders, setFilteredTenders] = useState([]);

  const { data: awardedTendersData = [], isLoading: awardedTendersIsLoading } =
    useGetApiQuery({ url: "tenders_awarded", lang: languageType });

  const { getText } = useLanguageTranslation();

  const lastUpdatedOn = useMemo(() => {
    return extractDateFromStr(
      getLatestUpdatedObject(awardedTendersData)?.changed
    );
  }, [awardedTendersData]);

  useEffect(() => {
    if (searchTerm) {
      setFilteredTenders(
        awardedTendersData.filter((tender) =>
          tender.title.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    } else {
      setFilteredTenders(awardedTendersData);
    }
  }, [searchTerm, awardedTendersData]);

  const { currentItems, currentPage, totalPages, handlePageChange } =
    usePagination(filteredTenders, 12);

  useTableResponsive(awardedTendersData);

  // for SEO
  const { pathname } = useLocation();
  const { innerBannerImage, innerBannerTitle } = useSeo({ pathname });

  if (awardedTendersIsLoading) {
    return <Loader />;
  }

  return (
    <>
      <InnerBannerBreadcrumb
        title={getText("Tender", "निविदा")}
        titleLink="/tender/tenders-awarded"
        levelTwo={true}
        levelThree={false}
        bannerImage={innerBannerImage}
        titleTwo={innerBannerTitle}
        isActive={innerBannerTitle}
      />

      <div className="inner-container">
        <div className="container">
          <div className="container">
            <div className="row">
              <div className="col-12 col-md-6 col-lg-6 mb-3 d-flex justify-content-between">
                <TenderSearch
                  setSearchTerm={setSearchTerm}
                  title={"Tender Number"}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-6 mb-3 d-flex justify-content-end">
                <PageLastUpdated lastUpdatedOn={lastUpdatedOn} />
              </div>
              <div className="col-12 col-sm-12 col-md-12 col-lg-12">
                <div className="table-responsive">
                  <table className="table table-striped custom-table">
                    <thead className="thead-dark">
                      <tr>
                        <th>{getText("S.No.", "क्र. सं.")}</th>
                        <th>{getText("Description", "विवरण")}</th>
                        <th>{getText("Remarks", "टिप्पणियों")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.length > 0 ? (
                        currentItems.map((item, index) => (
                          <tr key={index}>
                            <td data-key={getText("S.No.", "क्र. सं.")}>
                              {item.counter}
                            </td>
                            <td data-key={("Description", "विवरण")}>
                              <PdfZipItem
                                filePath={item?.field_description_file}
                                title={item.title}
                                filesize={item.filesize}
                              />
                            </td>
                            <td
                              className="no-wrap-td"
                              data-key={getText("Remarks", "टिप्पणियों")}
                            >
                              {getText(
                                "Details of Contract",
                                "अनुबंध का विवरण"
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="text-center">
                            No records found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AwardedTenders;
